// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./DeviceStatuses.scss";

import { collection, query, collectionGroup, onSnapshot } from "firebase/firestore";
import { AuthContext } from '../../context/AuthContext';
import { firestore } from "../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { startOfWeek, endOfWeek } from "date-fns";

import Navbar from '../../components/Navbar/Navbar';
import Sidebar from '../../components/Navbar/Sidebar';
import Dropdown from "../../components/Filters/Dropdown";
import TimeframeFilter from "../../components/Filters/TimeframeFilter";
import DevicesTable from "../AdminSettings/DevicesTable";
import Card from "../../components/Card/Card";
import DeviceModal from "../AdminSettings/DeviceModal/DeviceModal";

export default function AdminSettings() {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const { currentUser } = React.useContext(AuthContext);

    const [ clients, setClients ] = React.useState({});
    const [ selectedClient, setSelectedClient ] = React.useState({id: 0, name: "All"});
    const [ timeframe, setTimeframe ] = React.useState({startDate: startOfWeek(new Date()), endDate: endOfWeek(new Date()), label: "timeframe_this_week"});

    const [ devices, setDevices ] = React.useState({});
    const [ selectedDevice, setSelectedDevice ] = React.useState(null);
    const [ showDeviceModal, setShowDeviceModal ] = React.useState(false);

    const unsubscribeClients = React.useRef(null);
    const unsubscribeDevices = React.useRef(null);

    React.useEffect(() => {
        if (!currentUser || !currentUser.info || !currentUser.info.user_flags || !currentUser.info.user_flags.includes('admin')) {
            navigate("/")
        }
    }, [currentUser]);

    async function getClients() {
        if (unsubscribeClients.current) unsubscribeClients.current();

        const q = query(collection(firestore, "clients"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            const clientsNew = {};
            querySnapshot.forEach((doc) => {
                clientsNew[doc.id] = doc.data();
            });
            setClients(clientsNew);
        })
        unsubscribeClients.current = () => unsub();
    }

    async function getDevices() {
        if (unsubscribeDevices.current) unsubscribeDevices.current();

        const q = selectedClient.id==0 ? 
            query(collectionGroup(firestore, "devices")) :
            query(collection(firestore,"clients", selectedClient.id, "devices"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            const devicesNew = {};
            querySnapshot.forEach((doc) => {
                devicesNew[doc.id] = doc.data();
            });
            setDevices(devicesNew);
        });
        unsubscribeDevices.current = () => unsub();
    }

    React.useEffect(() => {
        getClients();
    }, []);
    React.useEffect(() => {
        getDevices();
    }, [selectedClient]);

    return (
        <div className="device-statuses">
            <Sidebar />
            <div className="device-statuses--right">
                <Navbar 
                    title={`${t("sidebar_device_statuses")}`}
                />
                <div className="client-header">
                    <Dropdown 
                        options={[
                        {id: 0, name: "All"},
                        ...Object.keys(clients).map(id => {
                            return {
                                id: id,
                                name: clients[id].company_displayname,
                            }
                        })]}
                        setOption={setSelectedClient}
                        defaultOption={{id: 0, name: "All"}}
                        title="Client"
                    />
                    <TimeframeFilter 
                        timeframe={timeframe}
                        setTimeframe={setTimeframe}
                    />
                </div>
                <DevicesTable devices={devices} setSelectedDevice={(device) => {setSelectedDevice(device); setShowDeviceModal(true);}}/>
                <div className="status-cards">
                    {Object.entries(devices).map(([id, device]) => {
                        const statusColor = device.status == "Online" ? "rgb(160, 235, 50)" : device.status == "Offline" ? "rgb(200, 50, 50)" : "rgb(200, 150, 50)";
                        return (<Card 
                            key={id}
                            cardData={{
                                ctype: 'DeviceStatusCard',
                                icon: <div 
                                    className="status-dot-icon"
                                    style={{backgroundColor: statusColor}}
                                />,
                                title: `${id} - ${clients?.[device.client]?.company_displayname?? device.client}`,
                                subtitle: `${device.display_name} - ${device.description}`,
                                client: device.client,
                                device: id,
                                timeframe: timeframe,
                            }}
                        />)
                    })}
                </div>
            </div>

            {showDeviceModal && <DeviceModal
                device = {selectedDevice}
                selectedClient = {selectedClient}
                clients = {clients}
                handleClose = {() => {setSelectedDevice(null); setShowDeviceModal(false);}}
            />}
        </div>
    )
}